import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TenantConfigService } from './tenant-config.service';

export interface AuthResponse {
    token: string;
    refreshToken: string;
    expiration: number;
    refreshExpiration: number;
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private readonly http = inject(HttpClient);
    private readonly router = inject(Router);
    private readonly tenantConfigService = inject(TenantConfigService);

    private readonly apiUrl = 'https://login.copago.io/api/v1/Auth/login';
    private readonly refreshUrl = 'https://login.copago.io/api/v1/Auth/refresh';
    private readonly userInfoUrl = 'https://login.copago.io/api/v1/User/Info';

    private refreshInFlight$?: Observable<AuthResponse>;

    login(username: string, password: string, app: number): Observable<AuthResponse> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json'
        });

        const body = {
            username,
            password,
            app
        };

        return this.http.post<AuthResponse>(this.apiUrl, body, { headers });
    }

    refreshToken(): Observable<AuthResponse> {
        if (this.refreshInFlight$) {
            return this.refreshInFlight$;
        }

        const refreshToken = this.getRefreshToken();
        const accessToken = this.getLastValidAccessToken() || this.getAccessToken();

        const headers = new HttpHeaders({
            'Content-Type': 'application/json'
        });

        this.refreshInFlight$ = this.http.post<AuthResponse>(
            this.refreshUrl,
            {
                accessToken,
                refreshToken
            },
            { headers }
        ).pipe(
            tap((response) => this.storeSession(response)),
            shareReplay(1),
            finalize(() => {
                this.refreshInFlight$ = undefined;
            })
        );

        return this.refreshInFlight$;
    }

    getAccessToken(): string {
        return localStorage.getItem('token') || '';
    }

    getLastValidAccessToken(): string {
        return localStorage.getItem('lastValidAccessToken') || '';
    }

    getRefreshToken(): string {
        return localStorage.getItem('refreshToken') || '';
    }

    getAccessTokenExpiration(): number {
        return Number(localStorage.getItem('tokenExpiration') || '0');
    }

    getRefreshTokenExpiration(): number {
        return Number(localStorage.getItem('refreshTokenExpiration') || '0');
    }

    hasAccessToken(): boolean {
        return !!this.getAccessToken();
    }

    hasRefreshToken(): boolean {
        return !!this.getRefreshToken();
    }

    isAccessTokenValid(bufferInSeconds = 30): boolean {
        const expiration = this.getAccessTokenExpiration();

        if (!expiration) {
            return false;
        }

        return Date.now() < expiration - bufferInSeconds * 1000;
    }

    isRefreshTokenValid(bufferInSeconds = 30): boolean {
        const expiration = this.getRefreshTokenExpiration();

        if (!expiration) {
            return false;
        }

        return Date.now() < expiration - bufferInSeconds * 1000;
    }

    canRefreshSession(): boolean {
        return this.hasRefreshToken() && this.isRefreshTokenValid();
    }

    hasUsableSession(): boolean {
        return this.hasAccessToken() && this.isAccessTokenValid();
    }

    needsRefresh(): boolean {
        return this.hasRefreshToken() && !this.isAccessTokenValid() && this.isRefreshTokenValid();
    }

    storeSession(response: AuthResponse): void {
        localStorage.setItem('token', response.token);
        localStorage.setItem('lastValidAccessToken', response.token);
        localStorage.setItem('refreshToken', response.refreshToken);
        localStorage.setItem('tokenExpiration', String(response.expiration));
        localStorage.setItem('refreshTokenExpiration', String(response.refreshExpiration));
        localStorage.setItem('admin_logged', 'true');

        this.tenantConfigService.setActiveCustomerByToken(response.token);

        /**
         * Always start each fresh login / refreshed session on the default tenant
         * of the resolved customer. Example:
         * ATG -> HP
         * DEV -> DEV 1
         */
        this.tenantConfigService.resetCurrentTenantToDefault();
    }

    getUserInfo(): Observable<unknown> {
        const token = this.getAccessToken();

        const headers = new HttpHeaders({
            Authorization: `Bearer ${token}`
        });

        return this.http.get<unknown>(this.userInfoUrl, { headers });
    }

    clearSession(): void {
        localStorage.removeItem('token');
        localStorage.removeItem('lastValidAccessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('tokenExpiration');
        localStorage.removeItem('refreshTokenExpiration');
        localStorage.removeItem('admin_logged');

        this.tenantConfigService.clearCustomerContext();
    }

    logout(): void {
        this.clearSession();
        this.router.navigate(['/login']);
    }

    ensureValidSession(): Observable<string> {
        if (this.hasUsableSession()) {
            return of(this.getAccessToken());
        }

        if (this.needsRefresh()) {
            return new Observable<string>((subscriber) => {
                this.refreshToken().subscribe({
                    next: (response) => {
                        subscriber.next(response.token);
                        subscriber.complete();
                    },
                    error: (error) => {
                        this.clearSession();
                        subscriber.error(error);
                    }
                });
            });
        }

        this.clearSession();
        return new Observable<string>((subscriber) => {
            subscriber.error(new Error('No valid session available.'));
        });
    }
}